import { Download, FileText, X } from 'lucide-react';
import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { Input } from '@/components/ui/Input/Input';
import { SingleDatePicker } from '@/components/ui';
import type { SettingsFormReturn } from '../useSettingsForm';

export function CertificatesCard({ form }: { form: SettingsFormReturn }) {
  const {
    certificates,
    certificateTitle,
    setCertificateTitle,
    certificateDate,
    setCertificateDate,
    selectedCertificateFile,
    uploadingCertificate,
    certificateFileInputRef,
    handleCertificateFileChange,
    handleUploadCertificate,
    toggleCertificatePrivacy,
    handleDownloadCertificate,
    handleDeleteCertificate,
  } = form;
  const shouldScroll = certificates.length > 3;
  return (
    <Box bg="white" borderRadius="2xl" p="6" boxShadow="0 8px 24px rgba(0,0,0,0.12)">
      <Text as="h3" fontSize="lg" fontWeight="semibold" color="volyx.primary" mb="4">
        Certificates
      </Text>
      <Box display="grid" gridTemplateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap="3" mb="3">
        <Input
          id="certificate-title"
          name="certificate-title"
          autoComplete="off"
          value={certificateTitle}
          onChange={(e) => setCertificateTitle(e.target.value)}
          placeholder="Certificate title"
        />
        <SingleDatePicker
          popover
          showAge
          id="certificate-date"
          name="certificate-date"
          autoComplete="off"
          value={certificateDate}
          onChange={(date) => setCertificateDate(date ?? '')}
          maxDate={new Date()}
          placeholder="Select Date"
        />
        <input
          ref={certificateFileInputRef}
          id="certificate-file-input"
          name="certificate-file"
          type="file"
          accept="application/pdf"
          style={{ display: 'none' }}
          onChange={handleCertificateFileChange}
        />
        <chakra.button
          type="button"
          px="6"
          py="3"
          borderRadius="xl"
          fontWeight="semibold"
          transition="all 0.3s"
          cursor="pointer"
          border="none"
          bg="volyx.lightGray"
          color="volyx.text"
          _hover={{ bg: 'volyx.mediumGray', transform: 'scale(1.01)' }}
          onClick={() => certificateFileInputRef.current?.click()}
        >
          Select PDF
        </chakra.button>
        <chakra.button
          type="button"
          px="6"
          py="3"
          borderRadius="xl"
          fontWeight="semibold"
          transition="all 0.3s"
          cursor="pointer"
          border="none"
          bg="volyx.action"
          color="white"
          _hover={{ bg: 'rgba(0,122,255,0.9)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', transform: 'translateY(-2px) scale(1.02)' }}
          _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
          onClick={handleUploadCertificate}
          disabled={!certificateTitle.trim() || !certificateDate || uploadingCertificate || !selectedCertificateFile}
        >
          {uploadingCertificate ? 'Uploading...' : 'Upload'}
        </chakra.button>
      </Box>
      <Text fontSize="xs" color="volyx.textSecondary" mb="4">
        Only PDF files are allowed (max 5MB)
      </Text>
      <Text fontSize="sm" color="volyx.textSecondary" mb="4">
        {selectedCertificateFile ? `Selected: ${selectedCertificateFile.name}` : 'No PDF selected'}
      </Text>

      <Flex
        flexDir="column"
        gap="3"
        role="region"
        aria-label="Certificates list"
        // Keep the page stable after a handful of certificates by enabling a scroll region.
        style={shouldScroll ? { overflowY: 'auto', maxHeight: '560px', paddingRight: '4px' } : undefined}
      >
        {certificates.length > 0 ? (
          certificates.map((cert) => (
            <Box
              key={cert.id}
              border="1px solid"
              borderColor="volyx.borderLight"
              borderRadius="xl"
              p="4"
            >
              <Box
                display="grid"
                gridTemplateColumns={{ base: '1fr', sm: '96px minmax(0, 1fr)' }}
                columnGap="4"
                rowGap="4"
              >
                <Flex
                  direction="column"
                  align="center"
                  justify="center"
                  gap="2"
                  border="1px solid"
                  borderColor="volyx.borderLight"
                  borderRadius="lg"
                  bg="volyx.lightGray"
                  minH="128px"
                  maxW="96px"
                  w="full"
                  px="3"
                  py="4"
                  alignSelf="start"
                  justifySelf={{ base: 'start', sm: 'stretch' }}
                >
                  <Box color="volyx.action">
                    <FileText size={32} />
                  </Box>
                  <Text fontSize="xs" fontWeight="semibold" color="volyx.textSecondary">
                    PDF
                  </Text>
                </Flex>
                <Flex direction="column" gap="3" minW="0" w="full">
                  <Box minW="0">
                    <Text as="h4" fontSize="md" fontWeight="semibold" color="volyx.primary" mb="1">
                      {cert.title}
                    </Text>
                    <Text fontSize="sm" color="volyx.textSecondary" mb="1">
                      {cert.date}
                    </Text>
                    <Text fontSize="sm" color="volyx.textSecondary">
                      {cert.organization || 'Self-uploaded'}
                    </Text>
                  </Box>
                  <Flex align="center" justify={{ base: 'stretch', sm: 'flex-end' }} gap="2" minW="0" w="full">
                    <chakra.button
                      type="button"
                      display="inline-flex"
                      alignItems="center"
                      justifyContent="center"
                      gap={{ base: '2', sm: '0' }}
                      flex={{ base: 1, sm: '0 0 auto' }}
                      minH={{ base: '10', sm: 'auto' }}
                      bg={{ base: 'volyx.lightGray', sm: 'transparent' }}
                      border="1px solid"
                      borderColor={{ base: 'volyx.borderLight', sm: 'transparent' }}
                      cursor="pointer"
                      color="volyx.action"
                      px={{ base: '3', sm: '2' }}
                      py="2"
                      borderRadius="lg"
                      transition="color 0.15s, background-color 0.15s, border-color 0.15s"
                      _hover={{ bg: 'volyx.lightGray', borderColor: 'volyx.borderLight' }}
                      onClick={() => handleDownloadCertificate(cert)}
                      aria-label={`Download ${cert.title}`}
                    >
                      <Download size={18} />
                      <Text as="span" fontSize="sm" fontWeight="medium" display={{ base: 'inline', sm: 'none' }}>
                        Download
                      </Text>
                    </chakra.button>
                    <chakra.button
                      type="button"
                      display="inline-flex"
                      alignItems="center"
                      justifyContent="center"
                      gap={{ base: '2', sm: '0' }}
                      flex={{ base: 1, sm: '0 0 auto' }}
                      minH={{ base: '10', sm: 'auto' }}
                      bg={{ base: 'rgba(255,59,48,0.08)', sm: 'transparent' }}
                      border="1px solid"
                      borderColor={{ base: 'rgba(255,59,48,0.14)', sm: 'transparent' }}
                      cursor="pointer"
                      color="volyx.error"
                      px={{ base: '3', sm: '2' }}
                      py="2"
                      borderRadius="lg"
                      transition="color 0.15s, background-color 0.15s, border-color 0.15s"
                      _hover={{ bg: 'rgba(255,59,48,0.1)', borderColor: 'rgba(255,59,48,0.2)' }}
                      onClick={() => handleDeleteCertificate(cert.id, cert.image_url)}
                      aria-label={`Delete ${cert.title}`}
                    >
                      <X size={18} />
                      <Text as="span" fontSize="sm" fontWeight="medium" display={{ base: 'inline', sm: 'none' }}>
                        Remove
                      </Text>
                    </chakra.button>
                  </Flex>
                  <Flex
                    align="center"
                    justify="space-between"
                    gap="3"
                    borderTop="1px solid"
                    borderColor="volyx.borderLight"
                    pt="3"
                    minW="0"
                    wrap="wrap"
                  >
                    <Flex align="center" gap="2">
                      <Text as="span" fontSize="sm" color="volyx.textSecondary">
                        {cert.is_public ? 'Public' : 'Private'}
                      </Text>
                      <Box as="label" position="relative" display="inline-block" w="11" h="6" cursor="pointer">
                        <chakra.input
                          type="checkbox"
                          id={`cert-${cert.id}-privacy`}
                          name={`cert-${cert.id}-privacy`}
                          checked={cert.is_public ?? true}
                          onChange={() => toggleCertificatePrivacy(cert.id, cert.is_public ?? true)}
                          opacity={0}
                          w="0"
                          h="0"
                          position="absolute"
                        />
                        <Box
                          position="absolute"
                          inset="0"
                          bg={(cert.is_public ?? true) ? 'volyx.action' : 'volyx.borderLight'}
                          borderRadius="full"
                          transition="all 0.2s"
                          _before={{
                            content: '""',
                            position: 'absolute',
                            h: '4',
                            w: '4',
                            left: '1',
                            bottom: '1',
                            bg: 'white',
                            borderRadius: 'full',
                            transition: 'transform 0.2s',
                            transform: (cert.is_public ?? true) ? 'translateX(1.25rem)' : 'translateX(0)',
                          }}
                        />
                      </Box>
                    </Flex>
                  </Flex>
                </Flex>
              </Box>
            </Box>
          ))
        ) : (
          <Text color="volyx.textSecondary" fontSize="md" textAlign="center" py="8">
            No certificates uploaded yet.
          </Text>
        )}
      </Flex>

    </Box>
  );
}
